import {
  AbstractMesh,
  BoundingBox,
  DeepImmutableObject,  
  Matrix,
  Mesh,
  Observer,
  PickingInfo,
  Quaternion,
  Ray,
  SceneLoader,
  TransformNode,
  Vector2,
  Vector3,
} from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { throwOnFrameTimeout } from '@modules/core/src/utils/FrameTimeout';
import { GLTFLoader, onPickedObservable, playAnimation, ZEDCamera } from '@modules/standard';
import zams from '@zams/logger/zed';
import { SdfFont, SdfFontAsset, SdfTextMesh, SdfTextParagraph } from '@zigbang/babylonjs-msdf-text';
import { World } from 'miniplex';

import { AlphaLayer, Entity as BaseEntity, DongEntity } from '..';
import { TourDanjiCameraAPI } from '../..';
import panelModel from '../../assets/model/UnitPanel.glb';
import fontMetadata from '../../assets/Pretendard-Bold-msdf/Pretendard-Bold-msdf.json';
import fontAtlas from '../../assets/Pretendard-Bold-msdf/Pretendard-Bold.png';
import { LayerMask, MAX_DISTANCE_FROM_UNIT_WINDOW } from '../../Constants';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { getTourDanjiMetaData, getTourDanjiZamsParams } from '../../TourDanjiModule';

import { ScreenDistanceEntity } from './ScreenDistanceSystem';
import { UnitModelSystem } from './UnitModelSystem';
import { UnitPriceEntity } from './UnitPriceSystem';
import { UnitEntity } from './UnitSystem';

export type UnitPanelEntity = {
  DongUnitPanels?: {
    root: TransformNode;
    panels: Mesh;
    labels: SdfTextMesh;
  };
  DongUnitPanelsVisible?: true;
  DongUnitPanelsDirty?: true;
  /**
   * 세대 패널들
   */
  UnitPanels?: {
    instanceId: number;
    position: Vector3;
    forward: Vector3;
    scale: Vector3;
  }[];
  /**
   * 선택된 패널을 강조하기 위한 이펙트
   */
  UnitPanelFX?: {
    position: Vector3;
    direction: Vector3;
    scale: Vector3;
    startTime: number;
  };
};

type Entity = UnitPanelEntity & UnitPriceEntity & UnitEntity & ScreenDistanceEntity & DongEntity & BaseEntity;

const infoLabel = {
  분양: '-',
  구분불가: '청약불가세대',
  임대: '거래불가세대',
  조합: '조합원세대',
  기타: '회사보유세대',
};

export class UnitPanelSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('UnitPanelSystem');
  private readonly scene;
  private readonly gltfLoader;
  private readonly world;
  private readonly withoutUnitPanel;
  private readonly withDongUnitPanels;
  private readonly withDongUnitPanelsDirty;
  private readonly withUnitPanel;
  private readonly materialManager;
  private readonly unitModelSystem;
  private readonly ray;

  private readonly cameraAPI;

  private readonly fontAsset;
  private panelMesh?: Mesh;
  private panelFXMesh?: AbstractMesh | undefined;
  private panelFXStartTime?: number | undefined = undefined;

  private readonly panelSize = new Vector3(2, 1, 1);
  private readonly options = {
    panel: {
      size: this.panelSize,
      maxScale: new Vector3(5, 2, 1).divide(this.panelSize),
      forwardOffset: 0.05,
      boundingBoxExtension: 10,
      minDistance: 300,
      minDistanceSquared: 300 ** 2,
      fadeDuration: 0.6,
    },
    label: {
      name: {
        size: Vector3.OneReadOnly.scale(0.45),
        offset: new Vector3(-0.9, 0.48, -0.1),
        textAlign: 'left',
        translate: Vector2.ZeroReadOnly,
      },
      info: {
        size: Vector3.OneReadOnly.scale(0.8),
        offset: new Vector3(0.9, -0.48, -0.1),
        textAlign: 'right',
        translate: new Vector2(-1, -1),
      },
    },
  } as const;

  protected _isVisible = false;
  public get isVisible() {
    return this._isVisible;
  }

  private onPickedObserver: Observer<PickingInfo> | undefined = undefined;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
    [GLTFLoader.INJECTABLE_TOKEN]: gltfLoader,
    [UnitModelSystem.INJECTABLE_TOKEN]: unitModelSystem,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
    [GLTFLoader.INJECTABLE_TOKEN]: GLTFLoader;
    [UnitModelSystem.INJECTABLE_TOKEN]: UnitModelSystem;
  }) {
    this.world = world;
    this.cameraAPI = cameraAPI;
    this.scene = babylonjs.scene;
    this.materialManager = materialManager;
    this.gltfLoader = gltfLoader;
    this.unitModelSystem = unitModelSystem;

    const withUnitCluster = world.with('UnitData', 'UnitCluster', 'TransformNode', 'parent');
    this.withoutUnitPanel = withUnitCluster.with('UnitPrice', 'ScreenDistance').without('UnitPanels');
    this.withUnitPanel = world.with('UnitData', 'UnitCluster', 'parent').with('UnitPanels');
    this.withDongUnitPanels = world.with('DongUnitPanels');
    this.withDongUnitPanelsDirty = this.withDongUnitPanels.with('DongUnitPanelsDirty');
    this.fontAsset = SdfFontAsset.From({ ...fontMetadata, pages: [fontAtlas] } as SdfFont, this.scene);

    this.onPickedObserver = onPickedObservable.add((pickingInfo) => {
      this.whenPicked(pickingInfo);
    });

    SceneLoader.LoadAssetContainerAsync(panelModel, undefined, undefined)
      .then((c) => {
        this.panelMesh = c.meshes[1] as Mesh;
        this.panelMesh.layerMask = LayerMask.Complex | LayerMask.Complex | LayerMask.Effect;
      })
      .catch(console.error);

    this.generatePanelFX();
    this.ray = new Ray(Vector3.ZeroReadOnly, Vector3.ZeroReadOnly, MAX_DISTANCE_FROM_UNIT_WINDOW);
  }

  private async generatePanelFX() {
    try {
      const result = await this.gltfLoader.loadModels<Entity>(
        this.scene,
        [
          {
            src: panelModel,
            modelName: '__root__',
          },
        ],
        undefined,
        {
          autoCompileShader: true,
          autoLoadTexture: true,
        }
      );

      const root = result.at(0)?.root;
      const meshes = result.at(0)?.model.meshes;
      if (!root || !meshes) {
        throw new Error('Failed to load panel fx model');
      }
      root.name = 'UnitPanelFX';

      this.panelFXMesh = meshes[1];
      if (this.panelFXMesh) {
        this.panelFXMesh.material = this.materialManager.createMaterial(MaterialType.UnitPanelFXMaterial);
        this.panelFXMesh.isVisible = false;
      }
    } catch (error) {
      console.error(error);
    }
  }

  private whenUpdate = () => {
    if (!this.panelMesh) return;

    for (const entity of this.withoutUnitPanel.entities.toSorted(
      (a, b) => a.ScreenDistance.distanceSquared - b.ScreenDistance.distanceSquared
    )) {
      throwOnFrameTimeout();

      const { UnitCluster, TransformNode: transformNode, UnitData, UnitPrice } = entity;
      const dongEntity = entity.parent as Entity;
      if (!dongEntity.DongData) continue;

      const { panels, labels } =
        dongEntity.DongUnitPanels ??
        (() => {
          const root = new TransformNode(`${dongEntity.DongData.meta.bd_id}`);
          const panels = this.panelMesh.clone('panel');
          panels.makeGeometryUnique(); // for thinInstance
          panels.alphaIndex = AlphaLayer.DANJI_UNIT_PANEL;
          panels.visibility = 0;
          panels.thinInstanceEnablePicking = true;
          const labels = new SdfTextMesh('label', this.scene, {
            font: this.fontAsset,
          });
          labels.visibility = 0;
          labels.layerMask = LayerMask.Complex | LayerMask.Building | LayerMask.Effect;

          root.setParent(dongEntity.TransformNode ?? null);
          panels.parent = root;
          labels.parent = root;

          const dongUnitPanels = {
            root,
            panels,
            labels,
          };

          this.world.addComponent(dongEntity, 'DongUnitPanels', dongUnitPanels);
          return dongUnitPanels;
        })();

      const newPanels = [];

      const shouldShowBuildingName = this.fontAsset.unsupportedChars(UnitData.meta.동).length === 0;
      const name = shouldShowBuildingName ? [UnitData.meta.동, UnitData.meta.호].join('\n') : UnitData.meta.호;

      const unitSupplyType = UnitPrice.presalePrices?.['공급유형'] as keyof typeof infoLabel;
      const info =
        (UnitPrice.price ?? 0) > 0 ? UnitPrice.formattedPrice ?? '' : infoLabel[unitSupplyType] ?? '정보없음';

      const nameParagraph = new SdfTextParagraph(name, this.fontAsset, this.options.label.name);
      const infoParagraph = new SdfTextParagraph(info, this.fontAsset, this.options.label.info);

      for (const edge of UnitCluster.edges) {
        if (!edge) continue;

        const position = edge.v1.add(edge.v2).scale(0.5).add(transformNode.getAbsolutePosition());

        const vec1 = edge.v2.subtract(edge.v1).normalize();
        const vec2 = Vector3.DownReadOnly;
        const forward = Vector3.Cross(vec2, vec1).normalize();

        /*TODO 계산된 forward(단지:6114 비스듬하게 나오는 이슈를 위해)가
        정반대쪽을 향하는 이슈(단지:10290 거래불가세대이 거꾸로 나옴)로 인해 실제
        kMean Edge.noraml와 비교하여 사이각이 90도 이하이면 forward 90도 이상이면 -forward 되도록 추가*/
        const dotProduct = Vector3.Dot(forward, edge.n1.negate());
        const angle = dotProduct >= 0 ? 0 : dotProduct <= -1 ? Math.PI : Math.acos(dotProduct);
        const shouldFlip = angle < Math.PI / 2;
        const correctedForward = shouldFlip ? forward.negate() : forward;
        const rotation = Quaternion.FromLookDirectionLH(correctedForward, Vector3.UpReadOnly);

        const scale = this.options.panel.maxScale
          .clone()
          .minimizeInPlaceFromFloats((edge.length - 1) / this.options.panel.size.x, Infinity, Infinity);
        position.addInPlace(correctedForward.scale(this.options.panel.forwardOffset));

        const panelMatrix = Matrix.Compose(scale, rotation, position);
        const instanceId = panels.thinInstanceAdd(panelMatrix, false);
        newPanels.push({
          instanceId,
          position,
          forward: correctedForward,
          scale,
        });

        labels.addParagraph(
          nameParagraph,
          Matrix.Compose(
            this.options.label.name.size,
            rotation,
            Vector3.TransformCoordinates(this.options.label.name.offset, panelMatrix)
          ),
          false
        );
        labels.addParagraph(
          infoParagraph,
          Matrix.Compose(
            this.options.label.info.size,
            rotation,
            Vector3.TransformCoordinates(this.options.label.info.offset, panelMatrix)
          ),
          false
        );
      }

      if (newPanels.length > 0) {
        this.world.addComponent(dongEntity, 'DongUnitPanelsDirty', true);
      }

      this.world.addComponent(entity, 'UnitPanels', newPanels);
    }
  };

  private isPicked = false;
  private whenPicked = async (pickingInfo: PickingInfo) => {
    if (getTourDanjiMetaData().disableInteriorView) {
      return;
    }
    // HACK: dirty hack to prevent double picking
    if (this.isPicked) return;
    this.isPicked = true;

    const panel = pickingInfo.pickedMesh?.name === 'panel' ? (pickingInfo.pickedMesh as Mesh) : undefined;
    const buildingId = panel?.parent?.name ? parseInt(panel.parent.name) : undefined;
    if (!buildingId || !panel?.visibility) {
      this.isPicked = false;
      return;
    }
    const unit = this.withUnitPanel.entities.find((entity) => {
      const building = entity.parent as Entity;
      if (building.DongData?.areaBuildingId !== buildingId) return false;
      return entity.UnitPanels.some((p) => p.instanceId === pickingInfo.thinInstanceIndex);
    });
    if (!unit) {
      this.isPicked = false;
      return;
    }

    zams.z151({
      ...getTourDanjiZamsParams(),
      zed_dong: unit.UnitData.meta.동,
      zed_ho: unit.UnitData.meta.호,
    }).send();

    this.unitModelSystem.enter(unit.UnitData);

    if (this.panelFXMesh && !this.panelFXMesh.isVisible) {
      const panelInstance = unit.UnitPanels.find((p) => p.instanceId === pickingInfo.thinInstanceIndex);
      if (panelInstance) {
        this.panelFXMesh.isVisible = true;
        this.panelFXMesh.position = panelInstance.position;
        this.panelFXMesh.lookAt(panelInstance.position.add(panelInstance.forward));
        this.panelFXMesh.scaling = panelInstance.scale;
        this.panelFXStartTime = Date.now();
      }
    }

    this.isPicked = false;
  };

  

  private updatePanelFX = () => {
    if (!this.panelFXMesh) return;
    if (!this.panelFXStartTime) return;

    if (Date.now() - this.panelFXStartTime < 1800) {
      const material = this.panelFXMesh.material;
      if (material) {
        material.alpha = (Math.cos(Date.now() * 0.012) + 1) * 0.2;
      }
    } else {
      this.panelFXMesh.isVisible = false;
      this.panelFXStartTime = undefined;
    }
  };

  private updatePanelBuffers = () => {
    for (const entity of this.withDongUnitPanelsDirty) {
      const { panels, labels } = entity.DongUnitPanels;
      panels.thinInstanceBufferUpdated('matrix');
      panels.setEnabled(panels.hasThinInstances);
      panels.freezeWorldMatrix();
      panels.thinInstanceRefreshBoundingInfo();
      panels.doNotSyncBoundingInfo = true;
      panels.alwaysSelectAsActiveMesh = true;

      labels.refresh();
      labels.freezeWorldMatrix();
      labels.thinInstanceRefreshBoundingInfo();
      labels.doNotSyncBoundingInfo = true;
      labels.alwaysSelectAsActiveMesh = true;

      this.world.removeComponent(entity, 'DongUnitPanelsDirty');
    }
  };

  private whenViewMatrixIdle = () => {
    const ray = this.cameraAPI.camera.getForwardRay(this.options.panel.minDistance, this.cameraAPI.camera._worldMatrix); // HACK: use cached world matrix to avoid call stack overflow

    // from ArcRotateCamera._getViewMatrix
    const betalessCameraPosition = ((camera: ZEDCamera) => {
      const cosa = Math.cos(camera.alpha);
      const sina = Math.sin(camera.alpha);
      const cosb = 0; // Math.cos(Math.PI  * 0.5);
      const sinb = 1; // Math.sin(Math.PI * 0.5);

      const target = camera['_getTargetPosition']();
      camera['_computationVector'].copyFromFloats(
        camera.radius * cosa * sinb,
        camera.radius * cosb,
        camera.radius * sina * sinb
      );

      // Rotate according to up vector
      if (camera['_upVector'].x !== 0 || camera['_upVector'].y !== 1.0 || camera['_upVector'].z !== 0) {
        Vector3.TransformCoordinatesToRef(
          camera['_computationVector'],
          camera['_yToUpMatrix'],
          camera['_computationVector']
        );
      }

      return target.add(camera['_computationVector']);
    })(this.cameraAPI.camera);

    let anyVisible = false;
    const forceHide =
      this.cameraAPI.getCurrentCameraViewType() === 'UnitWindowView' ||
      this.cameraAPI.getCurrentCameraViewType() === 'FakeUnitWindowView';
    for (const entity of this.withDongUnitPanels) {
      const unitPanelVisible = this.processPanelLod(entity, ray, betalessCameraPosition, forceHide);
      anyVisible ||= unitPanelVisible;
    }

    this._isVisible = anyVisible;
  };

  private processPanelLod = (
    entity: (typeof this.withDongUnitPanels.entities)[number],
    ray: Ray,
    cameraPosition: Vector3,
    forceHide: boolean
  ) => {
    const { root, panels, labels } = entity.DongUnitPanels;
    if (forceHide || entity.IsBlocking) {
      root.setEnabled(false);
      return false;
    } else {
      root.setEnabled(true);
    }
    const wasVisible = entity.DongUnitPanelsVisible ?? false;

    const boundingInfo = panels.getBoundingInfo();

    const distance = Vector3.DistanceSquared(
      // flatten y
      cameraPosition.multiplyByFloats(1, 0, 1),
      boundingInfo.boundingBox.centerWorld.multiplyByFloats(1, 0, 1)
    );

    let isVisible =
      distance < this.options.panel.minDistanceSquared &&
      ray.intersectsBox(boundingInfo.boundingBox as DeepImmutableObject<BoundingBox>, this.options.panel.boundingBoxExtension);

    if (wasVisible === isVisible) return isVisible;
    this.world.update(entity, 'DongUnitPanelsVisible', isVisible || undefined);

    const fadeFrom = panels.visibility;
    const fadeTo = isVisible ? 1 : 0;
    const remaining = Math.abs(fadeTo - fadeFrom);
    const duration = remaining * this.options.panel.fadeDuration;

    playAnimation(
      {
        target: [labels],
        property: {
          targetProperty: 'visibility',
          keyFrame: {
            duration: duration,
            from: fadeFrom,
            to: fadeTo,
          },
        },
      },
      undefined,
      true
    );

    const panelFade = isVisible ? 0.7 : 0;
    playAnimation(
      {
        target: [panels],
        property: {
          targetProperty: 'visibility',
          keyFrame: {
            duration: duration,
            from: fadeFrom,
            to: panelFade,
          },
        },
      },
      undefined,
      true
    );

    return isVisible;
  };

  dispose(): void {
    if (this.onPickedObserver) onPickedObservable.remove(this.onPickedObserver);
  }

  readonly operations: SystemOperationDefinition[] = [
    {
      group: 'viewMatrixIdle',
      run: this.whenViewMatrixIdle,
    },
    {
      group: 'beforeRender',
      run: this.whenUpdate,
    },
    {
      group: 'beforeRender',
      run: this.updatePanelBuffers,
    },
    {
      group: 'afterRender',
      run: this.updatePanelFX,
    },
  ];
}

